'use client'

import { cn } from '@/lib/utils/utils'
import { Partner } from '@/types/partner'
import { X } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import { getI18n, I18nDictionary, LangCode, useI18nValue } from '../i18n/lang-context'
import { closeBanner_clientSide, getInboundPartner_clientSide, isBannerOpened_clientSide } from '../partners/partner-inbound-link'
import { Button } from '../ui/button'
import { ScrollDirection, useScroll } from '../utils/windowScroll'
import { useScreen } from '../utils/windowSize'

// Translations
const I18N_BANNER: I18nDictionary = {
  message: {
    en: 'Welcome on behalf of {partner}', //, our valued partner
    fr: 'Bienvenue de la part de {partner}' //, notre partenaire de confiance
  }
}

export function SourceBanner({ langCode }: { langCode: LangCode }) {
  
  const i18n = getI18n(I18N_BANNER, langCode)
  const i18nValue = useI18nValue()

  const screen = useScreen()
  const { scrollDir, scrollY } = useScroll(1)
  const [shown, setShown] = useState(false)
  const [initialRender, setInitialRender] = useState(true)
  const [partner, setPartner] = useState<Partner>()
  const [isClosed, setIsClosed] = useState(false)

  let onMouseLeaveTimeoutFunc: ReturnType<typeof setTimeout> | null = null

  const show = useCallback(() => shown || scrollDir === ScrollDirection.up || scrollY < 100, [shown, scrollDir, scrollY])

  const handleMouseLeave = () => {
    if (onMouseLeaveTimeoutFunc) clearTimeout(onMouseLeaveTimeoutFunc)
    onMouseLeaveTimeoutFunc = setTimeout(() => setShown(false), 0)
  }

  const handleMouseEnter = () => {
    if (!screen.md) return
    if (onMouseLeaveTimeoutFunc) {
      clearTimeout(onMouseLeaveTimeoutFunc)
      onMouseLeaveTimeoutFunc = null
    }
    setShown(true)
  }

  const handleClose = () => {
    // Set cookie to remember banner was closed
    closeBanner_clientSide()
    setIsClosed(true)
  }

  useEffect(() => {
    // Check if banner was previously closed
    if(!isBannerOpened_clientSide()) {
      setIsClosed(true)
      return;
    }
    setPartner(getInboundPartner_clientSide())
  }, [])

  useEffect(() => {
    return () => {
      if (onMouseLeaveTimeoutFunc) clearTimeout(onMouseLeaveTimeoutFunc)
    }
  }, [])

  useEffect(() => {
    if (initialRender) {
      if (scrollY > 0) {
        setShown(true)
      }
      setInitialRender(false)
    }
  }, [scrollY, initialRender])

  // Don't render if no partner or banner was closed
  if (!partner || isClosed) return null

  const bannerTransform = {
    transform: `translateY(${show() ? 0 : 80}px)`,
    opacity: show() ? 1 : 0
  }

  // Prepare message
  let message = null
  let description = null
  if (partner) {
    message = i18n.message.replace('{partner}', partner.name)
    description = partner.description && i18nValue(partner.description, langCode)
  }

  return !partner ? <></> :
    <div className='fixed bottom-0 left-0 z-[60] w-full'
         onMouseLeave={handleMouseLeave}
         onMouseEnter={handleMouseEnter}>
      <div id='SOURCE_BANNER'
        className={cn(
          'w-full bg-primaryBright text-primary-foreground transition-all duration-300',
          show() ? 'shadow-lg' : ''
        )}
        style={bannerTransform}
      >
        <div className='m-auto flex w-full max-w-4xl items-center justify-between py-2'>
          <div className='h-max w-max pl-4 xl:pl-0'>
            <a href={partner.website[langCode]}
               target='_blank'
               rel='noopener noreferrer'
               className='flex h-12 items-center transition-opacity hover:opacity-80 md:h-16'>
              <Image src={partner.logo}
                     alt={partner.name}
                     width={partner.logoW || 150}
                     height={partner.logoH || 150}
                     className='h-full w-full object-contain'
                     style={{ maxHeight: '100%', maxWidth: '100%' }}/>
            </a>
          </div>
          <div>
            <p className='text-center text-base font-medium md:font-bold'>{message}</p>
            {screen.md && description &&
              <p className='text-center text-base font-medium'>
                “{description}”
              </p>
            }
          </div>
          <Button variant='ghost' size='sm'
                  onClick={handleClose}
                  className='hover:bg-primary-foreground/20 text-primary-foreground'
                  aria-label='Close banner'>
            <X className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
}
